import {isStoryProject} from '@reactorbox/shared';

import type {Asset, GenerationJob, OneOffVideoRequest, Project, Shot, Workspace} from '@reactorbox/shared';

export interface OneOffVideoResult {
  readonly asset?: Asset;
  readonly job: GenerationJob;
  readonly project: Project;
  readonly shot: Shot;
}

export const getStoryProjects = (workspace?: Workspace): Project[] =>
  workspace?.projects.filter(isStoryProject) || [];

export const getStoryProject = (workspace?: Workspace, selectedId?: string): Project | undefined => {
  const projects = getStoryProjects(workspace);
  return projects.find((project) => project.id === selectedId) || projects[0];
};

export const getOneOffVideoResults = (workspace: Workspace): OneOffVideoResult[] => {
  const results: OneOffVideoResult[] = [];
  for(const project of workspace.projects.filter((item) => item.workflow === 'one_off')) {
    for(const job of workspace.jobs.filter((item) => item.projectId === project.id && item.kind === 'video')) {
      const shot = project.shots.find((item) => item.id === job.shotId);
      if(shot) {
        results.push({asset: workspace.assets.find((item) => item.id === job.assetId), job, project, shot});
      }
    }
  }
  return results.sort((left, right) => right.job.createdAt.localeCompare(left.job.createdAt));
};

export const getOneOffVideoFingerprint = (video: Omit<OneOffVideoRequest, 'idempotencyKey'>): string => JSON.stringify([
  video.prompt.trim(), video.duration, video.resolution,
  video.referenceUrl?.trim() || '', video.title?.trim() || 'Untitled video'
]);

export const getPendingOneOffVideo = (
  workspace: Workspace,
  video: Omit<OneOffVideoRequest, 'idempotencyKey'>
): OneOffVideoResult | undefined => {
  const fingerprint = getOneOffVideoFingerprint(video);
  return getOneOffVideoResults(workspace).find(({job, project, shot}) =>
    ['queued', 'submitting', 'processing', 'reconciliation_required'].includes(job.status) &&
    getOneOffVideoFingerprint({
      duration: shot.duration,
      prompt: shot.description,
      referenceUrl: shot.referenceUrl,
      resolution: shot.resolution,
      title: project.name
    }) === fingerprint);
};
